import { useMemo } from 'react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { Campaign, CampaignAdGroup, CampaignAdGroupInventorySource } from '../../../types/Campaign';
import { InventorySource as DspInventorySource } from '../../../types/Inventory';
import ApiService from '../../../ApiService';

interface UseProps {
    campaign: Campaign;
}

interface UseState {
    targetQuery: UseQueryResult<DspInventorySource[]>;
    targetCount: number;
}

const useCampaignAdGroupInventorySourceSummary = ({ campaign }: UseProps): UseState => {
    const targetIds = useMemo(() => {
        return [
            ...new Set(
                campaign.adGroups
                    ?.flatMap((adGroup: CampaignAdGroup) => adGroup.adGroupInventorySources || [])
                    .map((adGroupTarget: CampaignAdGroupInventorySource) => adGroupTarget.dspInventoryId)
            ),
        ];
    }, [campaign.adGroups]);

    const targetQuery = useQuery<DspInventorySource[]>({
        queryKey: ['campaign/adGroupInventorySourceSummary', targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspInventorySources({
                ids: targetIds,
            });

            return data;
        },
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
    });

    const targetCount = useMemo((): number => {
        return targetQuery.data?.length || 0;
    }, [targetQuery.data]);

    return { targetQuery, targetCount };
};

export type { UseProps as CampaignAdGroupInventorySourceSummaryProps };
export type { UseState as CampaignAdGroupInventorySourceSummaryState };
export { useCampaignAdGroupInventorySourceSummary };
